<script setup lang="ts">
import { isEmpty } from 'lodash-es';
import { computed } from 'vue';

import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import { QA_TABLES } from '@modules/tables/constants';

import type { ITableMobileOrderEmits, ITableMobileOrderProps } from './types';
import VTableMobileMenu from '../VTableMobileMenu/VTableMobileMenu.vue';

const props = defineProps<ITableMobileOrderProps>();
const emit = defineEmits<ITableMobileOrderEmits>();

const availableColumns = computed(() => props.columns?.filter((col) => col.sort) ?? []);

function onOrder(columnName: string): void {
  if (props.disabled)
    return;
  emit('order-update', columnName);
}

const isSorted = computed<boolean>(() => (
  !isEmpty(props.tableState.order) && !Object.values(props.tableState.order).every((v) => v == null)
));

function isActive(columnName: string): boolean {
  return !!props.tableState.order?.[columnName];
}

function isOrderType(columnName: string, type: 'desc' | 'asc'): boolean {
  return props.tableState.order?.[columnName] === type;
}
</script>

<template>
  <VTableMobileMenu v-data-qa-auto-id="'VTableMobileOrder'" :columns="availableColumns">
    <template #default="{ openMenu }">
      <VIconButton
        v-data-test="{ el: QA_TABLES.mobileSortMenu }"
        icon="pi pi-sort-alt"
        class="text-muted-color"
        :class="{ '!text-primary': isSorted }"
        @click="openMenu($event)"
      />
    </template>
    <template #item="{ column }">
      <div
        v-data-test="{ el: QA_TABLES.headCellSort, columnName: column.name }"
        class="flex min-h-[42px] cursor-pointer items-center justify-between px-4 py-2 hover:bg-gray-100"
        :class="{ 'bg-primary-200': isActive(column.name), 'text-disabled-color': disabled }"
        @click="onOrder(column.name)"
      >
        <span>{{ column.label }}</span>
        <span class="text-primary">
          <span v-if="isOrderType(column.name, 'asc')">
            <i class="pi pi-sort-amount-up-alt text-xl" />
          </span>
          <span v-if="isOrderType(column.name, 'desc')">
            <i class="pi pi-sort-amount-down text-xl" />
          </span>
        </span>
      </div>
    </template>
  </VTableMobileMenu>
</template>
